"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiCreateDropPart } from "@/generated/models/ApiCreateDropPart";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBreakpoint } from "react-use";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "./drop.types";
import { DropLocation, hasDropFooter } from "./drop.types";
import { getRankHoverRingClass } from "./dropRankStyles";
import type { BoostAnimationState } from "./DropBoostAnimation";
import DropBoostAnimation from "./DropBoostAnimation";
import DropMinimalIdentityRow from "./DropMinimalIdentityRow";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
const GROUPING_TIME_DIFFERENCE_MS = 60_000;
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GROUPING_TIME_DIFFERENCE_MS;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(1)}`,
  2: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(2)}`,
  3: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(3)}`,
  default: "tw-ring-1 tw-ring-inset tw-ring-iron-700/35 desktop-hover:hover:tw-ring-iron-600/45"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-ring-1 tw-ring-inset tw-ring-[#3CCB7F]/55";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = isWaveView ? "" : "tw-ring-1 tw-ring-inset tw-ring-iron-800";
    const bgClass = isWaveView ? "" : "tw-bg-iron-950/80";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES];
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
const getContentOffsetClass = (compact: boolean): string => {
  if (compact) {
    return "tw-ml-11 tw-w-[calc(100%-2.5rem)]";
  }
  return "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]";
};
const shouldShowAuthorInfo = ({
  identityMode,
  shouldGroupWithPreviousDrop,
  isProfileView
}: {
  readonly identityMode: DropIdentityMode;
  readonly shouldGroupWithPreviousDrop: boolean;
  readonly isProfileView: boolean;
}): boolean => identityMode !== "hidden" && (!shouldGroupWithPreviousDrop || isProfileView || identityMode === "minimal");
const getGroupingClass = ({
  isProfileView,
  shouldGroupWithPreviousDrop,
  shouldGroupWithNextDrop
}: {
  readonly isProfileView: boolean;
  readonly shouldGroupWithPreviousDrop: boolean;
  readonly shouldGroupWithNextDrop: boolean;
}): string => {
  if (isProfileView) return "tw-py-4";
  if (shouldGroupWithPreviousDrop) return "tw-pt-1";
  if (shouldGroupWithNextDrop) return "tw-pt-4 tw-pb-1";
  return "tw-py-4";
};
const clearLongPressTimeout = ({
  longPressTimeoutRef
}: {
  readonly longPressTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
}) => {
  if (longPressTimeoutRef.current) {
    clearTimeout(longPressTimeoutRef.current);
  }
};
const handleTouchStartInternal = ({
  allowLongPress,
  isEditing,
  event,
  touchStartPosition,
  longPressTimeoutRef,
  onLongPress
}: {
  readonly allowLongPress: boolean;
  readonly isEditing: boolean;
  readonly event: React.TouchEvent;
  readonly touchStartPosition: React.RefObject<{
    x: number;
    y: number;
  } | null>;
  readonly longPressTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
  readonly onLongPress: () => void;
}) => {
  if (!allowLongPress || isEditing) {
    return;
  }
  const touch = event.touches[0];
  touchStartPosition.current = {
    x: touch!.clientX,
    y: touch!.clientY
  };
  longPressTimeoutRef.current = setTimeout(onLongPress, 500);
};
const handleTouchEndInternal = ({
  longPressTimeoutRef,
  touchStartPosition
}: {
  readonly longPressTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
  readonly touchStartPosition: React.RefObject<{
    x: number;
    y: number;
  } | null>;
}) => {
  clearLongPressTimeout({
    longPressTimeoutRef
  });
  touchStartPosition.current = null;
};
const handleTouchMoveInternal = ({
  allowLongPress,
  event,
  touchStartPosition,
  longPressTimeoutRef
}: {
  readonly allowLongPress: boolean;
  readonly event: React.TouchEvent;
  readonly touchStartPosition: React.RefObject<{
    x: number;
    y: number;
  } | null>;
  readonly longPressTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
}) => {
  if (!allowLongPress || !touchStartPosition.current) {
    return;
  }
  const touch = event.touches[0];
  const moveThreshold = 10;
  const deltaX = Math.abs(touch!.clientX - touchStartPosition.current.x);
  const deltaY = Math.abs(touch!.clientY - touchStartPosition.current.y);
  if (deltaX > moveThreshold || deltaY > moveThreshold) {
    clearLongPressTimeout({
      longPressTimeoutRef
    });
  }
};
const getAuthorHeader = ({
  showAuthorInfo,
  identityMode,
  drop,
  showWaveInfo,
  isStorm,
  activePartIndex,
  showActionsButton,
  handleOpenTouchActions,
  timestampLayout
}: {
  readonly showAuthorInfo: boolean;
  readonly identityMode: DropIdentityMode;
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly isStorm: boolean;
  readonly activePartIndex: number;
  readonly showActionsButton: boolean;
  readonly handleOpenTouchActions: (e: React.MouseEvent<HTMLButtonElement>) => void;
  readonly timestampLayout: DropTimestampLayout;
}): React.ReactNode => {
  if (!showAuthorInfo) {
    return null;
  }
  if (identityMode === "default") {
    return <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} showActionsButton={showActionsButton} onOpenActions={handleOpenTouchActions} timestampLayout={timestampLayout} />;
  }
  return <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} />;
};
const getContentBlock = ({
  shouldShowReplyHeader,
  onReplyClick,
  replyTo,
  drop,
  showAuthorInfo,
  authorHeader,
  shouldGroupWithPreviousDrop,
  isProfileView,
  activePartIndex,
  setActivePartIndex,
  handleLongPress,
  onDropContentClick,
  onQuoteClick,
  setLongPressTriggered,
  isEditing,
  isSaving,
  handleEditSave,
  handleEditCancel,
  allowLongPress,
  handleLinkCardActionsActiveChange,
  isMobile,
  showInteractions,
  showReplyAndQuote,
  handleOnReply,
  handleOnEdit,
  hasActiveLinkCardActions
}: {
  readonly shouldShowReplyHeader: boolean;
  readonly onReplyClick: (serialNo: number) => void;
  readonly replyTo: ExtendedDrop["reply_to"];
  readonly drop: ExtendedDrop;
  readonly showAuthorInfo: boolean;
  readonly authorHeader: React.ReactNode;
  readonly shouldGroupWithPreviousDrop: boolean;
  readonly isProfileView: boolean;
  readonly activePartIndex: number;
  readonly setActivePartIndex: (index: number) => void;
  readonly handleLongPress: () => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly setLongPressTriggered: (triggered: boolean) => void;
  readonly isEditing: boolean;
  readonly isSaving: boolean;
  readonly handleEditSave: (newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void;
  readonly handleEditCancel: () => void;
  readonly allowLongPress: boolean;
  readonly handleLinkCardActionsActiveChange: (actionId: string, active: boolean) => void;
  readonly isMobile: boolean;
  readonly showInteractions: boolean;
  readonly showReplyAndQuote: boolean;
  readonly handleOnReply: () => void;
  readonly handleOnEdit: () => void;
  readonly hasActiveLinkCardActions: boolean;
}): React.ReactNode => <>
    {shouldShowReplyHeader && replyTo && <WaveDropReply onReplyClick={onReplyClick} dropId={replyTo.drop_id} dropPartId={replyTo.drop_part_id} maybeDrop={replyTo.drop ? {
    ...replyTo.drop,
    wave: drop.wave
  } : null} />}
    <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">
      {showAuthorInfo && <WaveDropAuthorPfp drop={drop} />}
      <div className="tw-flex tw-w-full tw-flex-col" style={{
      maxWidth: showAuthorInfo ? "calc(100% - 3.5rem)" : "100%"
    }}>
        {authorHeader}
        <div className={`tw-w-full ${showAuthorInfo ? "tw-mt-2" : ""}${shouldGroupWithPreviousDrop && !isProfileView ? "tw-pl-[3.25rem]" : ""}`}>
          <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isEditing={isEditing} isSaving={isSaving} onSave={handleEditSave} onCancel={handleEditCancel} hasTouch={allowLongPress} onLinkCardActionsActiveChange={handleLinkCardActionsActiveChange} />
        </div>
      </div>
    </div>
    {!isMobile && showInteractions && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onEdit={handleOnEdit} suppressed={hasActiveLinkCardActions} />}
  </>;
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly wrapContentOnly?: ((content: React.ReactNode) => React.ReactNode) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
}
const WaveDrop = t0 => {
  const $ = _c(74);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    wrapContentOnly,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [activeLinkCardActionIds, setActiveLinkCardActionIds] = useState(t4);
  const [boostAnimation, setBoostAnimation] = useState(null);
  const dropRef = useRef(null);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  const shouldGroupWithPreviousDrop = !isDrop && shouldGroupWithDrop(drop, previousDrop);
  const shouldGroupWithNextDrop = !isDrop && shouldGroupWithDrop(drop, nextDrop);
  const isMobile = useIsMobileDevice();
  const hasTouch = useHasTouchInput() || isMobile;
  const breakpoint = useBreakpoint();
  const isMdUp = breakpoint === "MD";
  const allowLongPress = showInteractions && hasTouch && !isMdUp;
  const compact = useCompactMode();
  const hasActiveLinkCardActions = activeLinkCardActionIds.length > 0;
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = shouldShowAuthorInfo({
    identityMode,
    shouldGroupWithPreviousDrop,
    isProfileView
  });
  const showActionsButton = showInteractions && hasTouch && showReplyAndQuote && !isEditing && identityMode === "default";
  const groupingClass = getGroupingClass({
    isProfileView,
    shouldGroupWithPreviousDrop,
    shouldGroupWithNextDrop
  });
  const replyTo = drop.reply_to;
  const isGroupedReplyWithPrevious = shouldGroupWithPreviousDrop && replyTo?.drop_id === previousDrop?.reply_to?.drop_id;
  const shouldShowReplyHeader = !!replyTo && replyTo.drop_id !== dropViewDropId && !isGroupedReplyWithPrevious;
  let t5;
  if ($[1] !== allowLongPress || $[2] !== dispatch || $[3] !== editingDropId) {
    t5 = () => {
      if (!allowLongPress) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[1] = allowLongPress;
    $[2] = dispatch;
    $[3] = editingDropId;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleLongPress = t5;
  let t6;
  if ($[5] !== allowLongPress || $[6] !== handleLongPress || $[7] !== isEditing) {
    t6 = e => {
      handleTouchStartInternal({
        allowLongPress,
        isEditing,
        event: e,
        touchStartPosition,
        longPressTimeoutRef,
        onLongPress: handleLongPress
      });
    };
    $[5] = allowLongPress;
    $[6] = handleLongPress;
    $[7] = isEditing;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleTouchStart = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      handleTouchEndInternal({
        longPressTimeoutRef,
        touchStartPosition
      });
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleTouchEnd = t7;
  let t8;
  if ($[10] !== allowLongPress) {
    t8 = e_0 => {
      handleTouchMoveInternal({
        allowLongPress,
        event: e_0,
        touchStartPosition,
        longPressTimeoutRef
      });
    };
    $[10] = allowLongPress;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleTouchMove = t8;
  let t9;
  if ($[12] !== activePartIndex || $[13] !== dispatch || $[14] !== drop || $[15] !== editingDropId || $[16] !== onReply) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = dispatch;
    $[14] = drop;
    $[15] = editingDropId;
    $[16] = onReply;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleOnReply = t9;
  let t10;
  if ($[18] !== dispatch || $[19] !== editingDropId) {
    t10 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[18] = dispatch;
    $[19] = editingDropId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_1 => {
      e_1.stopPropagation();
      setLongPressTriggered(false);
      setIsSlideUp(true);
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleOpenTouchActions = t11;
  const authorHeader = getAuthorHeader({
    showAuthorInfo,
    identityMode,
    drop,
    showWaveInfo,
    isStorm,
    activePartIndex,
    showActionsButton,
    handleOpenTouchActions,
    timestampLayout
  });
  let t12;
  if ($[22] !== dispatch || $[23] !== drop.id) {
    t12 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[22] = dispatch;
    $[23] = drop.id;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleOnEdit = t12;
  let t13;
  if ($[25] !== activePartIndex || $[26] !== dispatch || $[27] !== drop || $[28] !== dropUpdateMutation) {
    t13 = (newContent, mentions, _mentionedGroups, mentionedWaves) => {
      const cleanedMentions = (mentions ?? drop.mentioned_users).map(_temp);
      const cleanedWaves = (mentionedWaves ?? drop.mentioned_waves).map(_temp2);
      const updatedParts = drop.parts.map((part, index) => {
        const attachments = (part.attachments ?? []).map(_temp3);
        const requestPart = {
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop ?? null,
          media: part.media
        };
        if (attachments.length) {
          requestPart.attachments = attachments;
        }
        return requestPart;
      });
      const updateRequest = {
        parts: updatedParts,
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        mentioned_waves: cleanedWaves,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[25] = activePartIndex;
    $[26] = dispatch;
    $[27] = drop;
    $[28] = dropUpdateMutation;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const handleEditSave = t13;
  let t14;
  if ($[30] !== dispatch) {
    t14 = () => {
      dispatch(setEditingDropId(null));
    };
    $[30] = dispatch;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const handleEditCancel = t14;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setBoostAnimation(null);
    };
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const handleBoostAnimationComplete = t15;
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = (actionId, active) => {
      setActiveLinkCardActionIds(current => {
        const hasActionId = current.includes(actionId);
        if (active) {
          return hasActionId ? current : [...current, actionId];
        }
        return hasActionId ? current.filter(item => item !== actionId) : current;
      });
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const handleLinkCardActionsActiveChange = t16;
  let t17;
  if ($[34] !== drop.context_profile_context?.boosted || $[35] !== drop.id) {
    t17 = () => {
      if (!dropRef.current) {
        return;
      }
      const rect = dropRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const isBoosted = drop.context_profile_context?.boosted ?? false;
      setBoostAnimation({
        id: `${drop.id}-${Date.now()}`,
        x,
        y,
        type: isBoosted ? "unboost" : "boost"
      });
    };
    $[34] = drop.context_profile_context?.boosted;
    $[35] = drop.id;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  drop.context_profile_context?.boosted;
  const handleMobileBoostAnimation = t17;
  let t18;
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => () => {
      clearLongPressTimeout({
        longPressTimeoutRef
      });
    };
    t19 = [];
    $[37] = t18;
    $[38] = t19;
  } else {
    t18 = $[37];
    t19 = $[38];
  }
  useEffect(t18, t19);
  const effectiveIsSlideUp = isSlideUp && !isEditing;
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  const contentBlock = getContentBlock({
    shouldShowReplyHeader,
    onReplyClick,
    replyTo,
    drop,
    showAuthorInfo,
    authorHeader,
    shouldGroupWithPreviousDrop,
    isProfileView,
    activePartIndex,
    setActivePartIndex,
    handleLongPress,
    onDropContentClick,
    onQuoteClick,
    setLongPressTriggered,
    isEditing,
    isSaving: dropUpdateMutation.isPending,
    handleEditSave,
    handleEditCancel,
    allowLongPress,
    handleLinkCardActionsActiveChange,
    isMobile,
    showInteractions,
    showReplyAndQuote,
    handleOnReply,
    handleOnEdit,
    hasActiveLinkCardActions
  });
  let t20;
  if ($[39] !== compact || $[40] !== drop || $[41] !== showInteractions) {
    t20 = (drop.metadata.length > 0 || showInteractions) && <div className={`tw-mx-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${getContentOffsetClass(compact)}`}>{drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />}{showInteractions && !!drop.raters_count && <WaveDropRatings drop={drop} />}{showInteractions && <WaveDropReactions drop={drop} />}</div>;
    $[39] = compact;
    $[40] = drop;
    $[41] = showInteractions;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const reactionsRow = t20;
  const shouldOffsetFooter = showAuthorInfo || shouldGroupWithPreviousDrop && !isProfileView;
  const footerOffsetClass = shouldOffsetFooter ? getContentOffsetClass(compact) : "";
  let t21;
  if ($[43] !== footer || $[44] !== footerOffsetClass) {
    t21 = hasDropFooter(footer) && <div className={`tw-mx-2 tw-mt-2 ${footerOffsetClass}`}>{footer}</div>;
    $[43] = footer;
    $[44] = footerOffsetClass;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const footerRow = t21;
  const t22 = `${isDrop && location === DropLocation.WAVE ? "tw-px-4 tw-py-0.5" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  const t23 = wrapContentOnly ? wrapContentOnly(contentBlock) : contentBlock;
  let t24;
  if ($[46] !== drop || $[47] !== effectiveIsSlideUp || $[48] !== handleMobileBoostAnimation || $[49] !== handleOnAddReaction || $[50] !== handleOnEdit || $[51] !== handleOnReply || $[52] !== longPressTriggered || $[53] !== showInteractions || $[54] !== showReplyAndQuote) {
    t24 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={effectiveIsSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} onBoostAnimation={handleMobileBoostAnimation} />;
    $[46] = drop;
    $[47] = effectiveIsSlideUp;
    $[48] = handleMobileBoostAnimation;
    $[49] = handleOnAddReaction;
    $[50] = handleOnEdit;
    $[51] = handleOnReply;
    $[52] = longPressTriggered;
    $[53] = showInteractions;
    $[54] = showReplyAndQuote;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== boostAnimation || $[57] !== handleBoostAnimationComplete) {
    t25 = <DropBoostAnimation animation={boostAnimation} onComplete={handleBoostAnimationComplete} />;
    $[56] = boostAnimation;
    $[57] = handleBoostAnimationComplete;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== drop.serial_no || $[60] !== drop.stableHash || $[61] !== dropClasses || $[62] !== footerRow || $[63] !== handleTouchEnd || $[64] !== handleTouchMove || $[65] !== handleTouchStart || $[66] !== reactionsRow || $[67] !== t23 || $[68] !== t24 || $[69] !== t25) {
    t26 = <div ref={dropRef} className={dropClasses} data-wave-drop-id={drop.stableHash} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t23}{reactionsRow}{footerRow}{t24}{t25}</div>;
    $[59] = drop.serial_no;
    $[60] = drop.stableHash;
    $[61] = dropClasses;
    $[62] = footerRow;
    $[63] = handleTouchEnd;
    $[64] = handleTouchMove;
    $[65] = handleTouchStart;
    $[66] = reactionsRow;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t22 || $[72] !== t26) {
    t27 = <div className={t22}>{t26}</div>;
    $[71] = t22;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  return t27;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}
function _temp2(wave) {
  return {
    wave_id: wave.wave_id,
    wave_name_in_content: wave.wave_name_in_content
  };
}
function _temp3(attachment) {
  return {
    attachment_id: attachment.attachment_id
  };
}